import isNil from "lodash/isNil";
import { getAttribute } from "PFCore/helpers/get_attribute";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { AccessLevel, Activity, BookingCategory, Id } from "PFTypes";
import { useEffect, useRef } from "react";
import { FieldPath, UseFormSetValue } from "react-hook-form";

import { BookingFormMode } from "../booking_form.types";
import { truncateTitle } from "../booking_form_common_inputs.utils";
import { BookingFormValues } from "../booking_form_values.types";

type UpdateTitleAndCategory = {
  titleKey: FieldPath<BookingFormValues>;
  categoryKey: FieldPath<BookingFormValues>;
  oldTitle: string | undefined;
  oldCategory: BookingCategory | null | undefined;
  selectedActivity: Activity;
};

type UseEngagementTitleAndCategorySyncParams = {
  mode: BookingFormMode;
  selectedActivity: Activity | undefined;
  isFetchingActivity: boolean;
  globalTitle: string | undefined;
  globalCategory: BookingCategory | null | undefined;
  bookings: BookingFormValues["bookings"];
  setValue: UseFormSetValue<BookingFormValues>;
};

export const useEngagementTitleAndCategorySync = ({
  mode,
  selectedActivity,
  isFetchingActivity,
  globalTitle,
  globalCategory,
  bookings,
  setValue
}: UseEngagementTitleAndCategorySyncParams) => {
  const { getBookingCategory } = useBookingCategories();
  const prevEngagementData = useRef<{ name: string | null; bookingCategoryId: Id | null }>({
    name: null,
    bookingCategoryId: null
  });

  const updateTitleAndCategory = ({
    titleKey,
    categoryKey,
    oldTitle,
    oldCategory,
    selectedActivity
  }: UpdateTitleAndCategory) => {
    const prevCategory = getBookingCategory(prevEngagementData.current.bookingCategoryId);
    const newCategory = getBookingCategory(getAttribute(selectedActivity, "booking_category_id"));

    const isTitlePreviousEngagementName = oldTitle === prevEngagementData.current.name;
    const isTitlePreviousCategoryName = oldTitle === getAttribute(prevCategory, "displayAs");
    const isCategoryPreviousEngagementCategory =
      prevEngagementData.current.bookingCategoryId === oldCategory?.id;

    if (!oldTitle || isTitlePreviousEngagementName || isTitlePreviousCategoryName) {
      const newTitle = selectedActivity.private
        ? getAttribute(newCategory, "displayAs")
        : selectedActivity.name;
      setValue(titleKey, truncateTitle(newTitle));
    }
    if (!isNil(newCategory) && (!oldCategory || isCategoryPreviousEngagementCategory)) {
      const isPermittedToWrite = getAttribute(newCategory, "bookingsPermissions") === AccessLevel.ReadWrite;
      isPermittedToWrite &&
        setValue(categoryKey, newCategory, {
          shouldValidate: true
        });
    }
  };

  useEffect(() => {
    if (isFetchingActivity) {
      return;
    }
    if (!selectedActivity) {
      prevEngagementData.current = {
        name: null,
        bookingCategoryId: null
      };
      return;
    }

    if (mode === BookingFormMode.Create) {
      updateTitleAndCategory({
        selectedActivity,
        oldTitle: globalTitle,
        oldCategory: globalCategory,
        categoryKey: "globalCategory",
        titleKey: "globalTitle"
      });
    } else {
      bookings.forEach((booking, index) => {
        updateTitleAndCategory({
          selectedActivity,
          oldTitle: booking.title,
          oldCategory: booking.category,
          categoryKey: `bookings.${index}.category`,
          titleKey: `bookings.${index}.title`
        });
      });
    }
    prevEngagementData.current = {
      name: selectedActivity.name,
      bookingCategoryId: getAttribute(selectedActivity, "booking_category_id") ?? null
    };
  }, [selectedActivity, isFetchingActivity]);
};
